import { useQuery } from "@apollo/client";
import { extractKeyedSearchTerms } from "encamp-omnisearch/src/utils/keyedSearchTerms";
import { useOmnisearchActions, useOmnisearchStore } from "hooks/omnisearch-v2";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { addOrUpdateKeyInOmnisearch, removeKeyInOmnisearch } from "utils/omnisearchUtils";

import { LATEST_INVOICE_DATE_QUERY } from "./invoiceTrackingQueries";

export const PERIOD_KEY = "invoiceDate";

/** Inclusive on both ends, as `YYYY-MM-DD`. Empty until a default is known. */
export type InvoiceTrackingPeriod = { from: string; through: string };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A calendar month is the period vendors bill in. An empty month has no bounds
 * to state, and the tabs ask for nothing until one is known.
 */
export function monthBounds(month: string): InvoiceTrackingPeriod {
  if (!/^\d{4}-\d{2}$/.test(month)) return { from: "", through: "" };
  const [year, index] = month.split("-").map(Number);
  const lastDay = new Date(Date.UTC(year ?? 0, index ?? 0, 0)).getUTCDate();
  return { from: `${month}-01`, through: `${month}-${String(lastDay).padStart(2, "0")}` };
}

/** The period a search states, or empty bounds when it states none this bar can show. */
export function periodFromSearch(
  search: string,
  periodKey: "invoiceDate" | "schedulePeriod" = PERIOD_KEY
): InvoiceTrackingPeriod {
  const [terms] = extractKeyedSearchTerms(search);
  const dates = terms.filter(({ key }) => key === periodKey);
  const value = dates.length === 1 && dates[0]?.value.length === 1 ? dates[0].value[0] : undefined;
  const empty = { from: "", through: "" };
  if (!value) return empty;
  const parts = value.includes("..") ? value.split("..") : [value, value];
  if (parts.length !== 2) return empty;
  const [from, through] = parts;
  if (!from || !through || !ISO_DATE.test(from) || !ISO_DATE.test(through)) return empty;
  return { from, through };
}

/** Convert old schedule links to the date term shared by all three tabs. */
export function normalizeInvoiceTrackingSearch(search: string): string {
  const [terms] = extractKeyedSearchTerms(search);
  if (!terms.some(({ key }) => key === "schedulePeriod")) return search;
  const normalized = removeKeyInOmnisearch(search, "schedulePeriod");
  if (terms.some(({ key }) => key === PERIOD_KEY)) return normalized;
  const { from, through } = periodFromSearch(search, "schedulePeriod");
  return from && through
    ? addOrUpdateKeyInOmnisearch(normalized, PERIOD_KEY, `${from}..${through}`)
    : normalized;
}

/** Schedule Adherence receives the range as arguments rather than a row filter. */
export function withoutPeriodTerm(search: string): string {
  return removeKeyInOmnisearch(search, PERIOD_KEY);
}

/**
 * The period as a token the grids hide from their filter pills; the bar above
 * the tabs is its one control.
 */
export function periodDisplayTokens(
  period: InvoiceTrackingPeriod
): readonly { key: string; value: string }[] {
  if (!period.from || !period.through) return [];
  return [{ key: PERIOD_KEY, value: `${period.from}..${period.through}` }];
}

/**
 * Reads only the search: a pill exists only for a term that is there, so the
 * default period is irrelevant.
 */
export function usePeriodDisplayTokens(): readonly { key: string; value: string }[] {
  const { omnisearch } = useOmnisearchStore();
  return useMemo(() => periodDisplayTokens(periodFromSearch(omnisearch)), [omnisearch]);
}

/** The period alone, as a query string for a link that must carry it. */
export function periodSearch(period: InvoiceTrackingPeriod): string {
  if (!period.from || !period.through) return "";
  const omnisearch = `${PERIOD_KEY}:${period.from}..${period.through}`;
  return `?${new URLSearchParams({ omnisearch })}`;
}

/** Clears every filter but the period, which the bar above the tabs still owns. */
export function useClearFiltersToPeriod(): () => void {
  const { omnisearch } = useOmnisearchStore();
  const { setOmnisearchImmediate } = useOmnisearchActions();
  return useCallback(() => {
    const { from, through } = periodFromSearch(omnisearch);
    setOmnisearchImmediate(from && through ? `${PERIOD_KEY}:${from}..${through}` : "");
  }, [omnisearch, setOmnisearchImmediate]);
}

/** Why the chosen range cannot be used, or null. Shown against the picker. */
export function periodError(period: InvoiceTrackingPeriod): string | null {
  const { from, through } = period;
  if (from === "" || through === "") return null;
  if (![from, through].every((date) => DateTime.fromISO(date, { zone: "utc" }).isValid)) {
    return "Choose valid start and end dates.";
  }
  return from > through ? "The period ends before it starts." : null;
}

/** The layout chooses one default and persists it before mounting any list. */
export function useInvoiceTrackingPeriod(enabled = true) {
  const { tenantId } = useTenant();
  const { omnisearch } = useOmnisearchStore();
  const { editKeyValues } = useOmnisearchActions();
  const stated = useMemo(
    () => periodFromSearch(normalizeInvoiceTrackingSearch(omnisearch)),
    [omnisearch]
  );
  const hasStatedPeriod = Boolean(stated.from && stated.through);
  const query = useQuery(LATEST_INVOICE_DATE_QUERY, {
    variables: { tenantId: tenantId ?? "" },
    skip: !tenantId || !enabled || hasStatedPeriod,
  });

  const latestInvoiceDate = query.data?.wasteInvoices.items[0]?.invoiceDate;
  // An empty tenant still gets a bounded query and can show its empty state.
  const invoiceMonth = query.data
    ? (latestInvoiceDate?.slice(0, 7) ?? DateTime.utc().toFormat("yyyy-MM"))
    : "";
  const period = hasStatedPeriod ? stated : monthBounds(invoiceMonth);

  const setPeriod = useCallback(
    (next: { from?: string; through?: string }) => {
      const { from, through } = next;
      editKeyValues([[PERIOD_KEY, from && through ? `${from}..${through}` : ""]]);
    },
    [editKeyValues]
  );

  return {
    period,
    setPeriod,
    ready: Boolean(period.from && period.through),
    error: hasStatedPeriod ? undefined : query.error,
    retry: () => {
      void query.refetch().catch(() => undefined);
    },
  };
}

/** Builds an Invoice Tracking link that keeps the selected period. */
export function useInvoiceTrackingLink(): (path: string) => string {
  const { tenantId } = useTenant();
  const [params] = useSearchParams();
  const search = periodSearch(
    periodFromSearch(normalizeInvoiceTrackingSearch(params.get("omnisearch") ?? ""))
  );
  return useCallback(
    (path: string) => `/o/${tenantId}/waste/invoices${path}${search}`,
    [tenantId, search]
  );
}

/**
 * Schedule Adherence narrowed to one billed service order, over the month its
 * service fell in. A wider range would always find a visit answering the
 * neighbouring month's window, but grades the months together and misreads an
 * uninvoiced month as missed service. The order is a keyed, quoted term: bare
 * text also matches stores and manifests, and a colon in it reads as a filter key.
 */
export function scheduleOrderLink(
  tenantId: string,
  serviceDate: string | null | undefined,
  serviceOrderNumber: string
): string | null {
  const period = monthBounds(serviceDate?.slice(0, 7) ?? "");
  if (!period.from) return null;
  const order = serviceOrderNumber.replaceAll('"', "");
  const omnisearch = `${PERIOD_KEY}:${period.from}..${period.through} serviceOrderNumber:"${order}"`;
  return `/o/${tenantId}/waste/invoices/schedule-adherence?${new URLSearchParams({ omnisearch })}`;
}
